import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../Input';
import { Button } from '../Button';
import { Icon } from '../Icon';
import CustomLink from '../CustomLink';
import { PRIVACY_POLICY_LINK } from '../../shared/links';
import { useSendResults } from '../../shared/use-send-results';
import styles from './EmailSlideV2.css';

function EmailSlideV2({
    setEmail,
    onContinue,
    results,
    title = 'Enter your email to start your Boundly story',
    description = '',
    placeholder = 'Enter their personal email',
    agreementText = 'We respect your privacy and are committed to protecting your personal data. Your data will be processed in accordance with our ',
    legalLinks = { privacyPolicy: PRIVACY_POLICY_LINK }
}) {
    const emailRef = useRef(null);
    const afterSuccessfulSendResults = callback => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        callback(); /* eslint-disable-line */
        onContinue();
    };

    const { fields, changeField, isPending, submitForm, errors } = useSendResults({ saveEmail: setEmail, afterCallback: afterSuccessfulSendResults, initialPrelander: results?.version });
    const isDidYouMean = Boolean(errors.form?.didYouMean);

    useEffect(() => {
        setTimeout(() => {
            emailRef.current?.focus();
        }, 50);
    }, []);

    return (
        <form
            className={styles.root}
            onSubmit={submitForm}
        >
            <div className={styles.head}>
                <h1 className={styles.title}>{title}</h1>
                {Boolean(description) && <p className={styles.description}>{description}</p>}
            </div>
            <Input
                value={fields.email}
                name='email'
                onChange={changeField}
                className={styles.input}
                placeholder={placeholder}
                errorText={errors.email || (!isDidYouMean && errors.form?.message)}
                disabled={isPending}
                inputProps={{ autoComplete: 'email', type: 'email', inputMode: 'email' }}
                emailDomainSuggestions={{
                    list: ['gmail.com', 'yahoo.com', 'icloud.com', 'hotmail.com', 'aol.com', 'outlook.com', 'comcast.net', 'att.net', 'live.com', 'mail.com', 'msn.com']
                }}
                inputRef={emailRef}
                didYouMean={errors.form?.didYouMean}
            />
            <div className={styles.agreementBlock}>
                <Icon name='lockSolid' />
                <span>
                    {agreementText}
                    <CustomLink
                        to={legalLinks.privacyPolicy}
                        target='_blank'
                    >
                        Privacy&nbsp;Policy
                    </CustomLink>
                    .
                </span>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerWrapper}>
                    <Button
                        type='pine'
                        label='Continue'
                        isLoading={isPending}
                        inSlide
                        buttonProps={{ type: 'submit' }}
                    />
                </div>
            </div>
        </form>
    );
}

EmailSlideV2.propTypes = {
    setEmail: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    results: PropTypes.shape({ version: PropTypes.string }),
    title: PropTypes.string,
    description: PropTypes.string,
    placeholder: PropTypes.string,
    agreementText: PropTypes.string,
    legalLinks: PropTypes.object
};

export {
    EmailSlideV2
};
